/**
 * Standardized API Error Contract
 * Imported from shared @repo/types package (single source of truth)
 */
import type {
  ErrorCode,
  ApiErrorResponse,
  ApiSuccessResponse,
  ApiResponse,
} from "@repo/types"

// Re-export for consumers
export type { ErrorCode, ApiErrorResponse, ApiSuccessResponse, ApiResponse }

/**
 * Check if response is an error
 */
export function isApiError<T>(
  response: ApiResponse<T>
): response is ApiErrorResponse {
  return !response.success
}

/**
 * Error code to translation key mapping
 * Allows frontend to override backend messages if needed
 */
export const ERROR_CODE_TO_I18N_KEY: Record<ErrorCode, string> = {
  AUTH_INVALID_CREDENTIALS: "errors.invalidCredentials",
  AUTH_TOKEN_EXPIRED: "errors.tokenExpired",
  AUTH_TOKEN_INVALID: "errors.unauthorized",
  AUTH_UNAUTHORIZED: "errors.unauthorized",
  AUTH_FORBIDDEN: "errors.forbidden",
  AUTH_USER_EXISTS: "errors.userExists",
  AUTH_USER_NOT_FOUND: "errors.userNotFound",
  AUTH_SESSION_EXPIRED: "errors.sessionExpired",
  VALIDATION_ERROR: "errors.validationError",
  VALIDATION_REQUIRED_FIELD: "errors.required",
  VALIDATION_INVALID_FORMAT: "errors.invalidInput",
  VALIDATION_MIN_LENGTH: "errors.minLength",
  VALIDATION_MAX_LENGTH: "errors.maxLength",
  RESOURCE_NOT_FOUND: "errors.notFound",
  RESOURCE_CONFLICT: "errors.conflict",
  RESOURCE_ALREADY_EXISTS: "errors.userExists",
  INTERNAL_ERROR: "errors.serverError",
  SERVICE_UNAVAILABLE: "errors.serviceUnavailable",
  RATE_LIMIT_EXCEEDED: "errors.rateLimit",
  FEATURE_DISABLED: "errors.featureDisabled",
  BUSINESS_RULE_VIOLATION: "errors.badRequest",
  INSUFFICIENT_PERMISSIONS: "errors.forbidden",
  OPERATION_NOT_ALLOWED: "errors.forbidden",
  AUTH_EMAIL_NOT_VERIFIED: "errors.emailNotVerified",
  AUTH_OTP_INVALID: "errors.otpInvalid",
  AUTH_OTP_EXPIRED: "errors.otpExpired",
  AUTH_OTP_LOCKED: "errors.otpLocked",
  AUTH_OTP_RATE_LIMITED: "errors.otpRateLimited",
  AUTH_WEAK_PASSWORD: "errors.weakPassword",
  AUTH_ACCOUNT_LOCKED: "errors.accountLocked",
}

/**
 * HTTP status code to error code mapping (fallback when backend code missing)
 */
export const STATUS_CODE_TO_ERROR_CODE: Record<number, ErrorCode> = {
  400: "VALIDATION_ERROR",
  401: "AUTH_UNAUTHORIZED",
  403: "AUTH_FORBIDDEN",
  404: "RESOURCE_NOT_FOUND",
  409: "RESOURCE_CONFLICT",
  422: "VALIDATION_ERROR",
  429: "RATE_LIMIT_EXCEEDED",
  500: "INTERNAL_ERROR",
  503: "SERVICE_UNAVAILABLE",
}
